import { useCallback, useEffect, useRef, useState } from "react";

import { parseBlsMessage, type BlsMessage } from "../domain/blsChannel";
import { EmdrBoardAPI } from "../infrastructure/api/EmdrBoard.api";
import { useBlsSession, type BlsChannel } from "./useBlsSession";
import { BOARD_IDENTITIES } from "./useBroadcastBlsChannel";
import { createSerialQueue, useRelayPoll } from "./useRelayPoll";

const encoder = new TextEncoder();

/** Failed reads in a row (about 3s apart) before the board counts as gone. */
const FAILURES_BEFORE_ALONE = 3;

/**
 * The patient's side of a shared board, from the link alone.
 *
 * Posts what the patient says — their stop, their controllers — and reads the
 * board's messages every second. If the board stops polling (the therapist
 * closed it, or lost their connection) the set is taken down here: a stimulus
 * nobody is driving is the one state this tool must never be left in.
 */
export function useRelayBlsChannel(token: string, cursor: number | null): BlsChannel {
    const identity = BOARD_IDENTITIES.patient;
    // The patient's messages in the order they were said: a stop must not be
    // overtaken by what came before it.
    const queue = useRef(createSerialQueue()).current;
    const send = useCallback(
        (message: BlsMessage) =>
            queue(async () => {
                await EmdrBoardAPI.postAsPatient(token, JSON.stringify(message));
            }),
        [queue, token]
    );
    const readIdentity = useCallback(() => identity, [identity]);
    const session = useBlsSession(send, readIdentity);
    const { receive, announce, peerLeft } = session;
    const [therapistPresent, setTherapistPresent] = useState<boolean | null>(null);
    const wasPresent = useRef(false);
    const failures = useRef(0);

    useRelayPoll({
        cursor,
        fetch: async (after) => (await EmdrBoardAPI.messagesForPatient(token, after)).data,
        onMessage: (payload, sender) => {
            const message = parseBlsMessage(encoder.encode(payload));
            if (message) receive(message, sender);
        },
        onPresence: (present) => {
            failures.current = 0;
            setTherapistPresent(present);
        },
        // Unable to reach our server is unable to hear the board: after a few
        // failed reads in a row the set comes down here, as if the therapist
        // had left — never a dot running that nobody can stop.
        onError: () => {
            failures.current += 1;
            if (failures.current >= FAILURES_BEFORE_ALONE) setTherapistPresent(false);
        },
    });

    // Ask for the set as soon as we can read the answer.
    useEffect(() => {
        if (cursor !== null) announce();
    }, [announce, cursor]);

    useEffect(() => {
        if (therapistPresent === null) return;
        if (therapistPresent && !wasPresent.current) announce();
        if (!therapistPresent && wasPresent.current) peerLeft(BOARD_IDENTITIES.therapist);
        wasPresent.current = therapistPresent;
    }, [announce, peerLeft, therapistPresent]);

    return session;
}
